import { inject, Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, finalize } from 'rxjs';
import {
  AlertsResponse,
  Flow,
  Gate,
} from '@common/interfaces/alerts/alerts.interface';
import { AlertStateEnum } from '@common/enums/alert-state.enum';
import { AlertActionEnum } from '@common/enums/alert-action.enum';
import { AlertTypeEnum } from '@common/enums/alert-type.enum';
import { AuthService } from '@common/services/auth/auth.service';
import { API_ENDPOINTS } from '@common/utils/api-endpoints.const';
import { ROUTE_PATHS } from '@common/utils/route-paths.const';
import { actionKeyToRoute } from './action-route.util';

@Injectable({
  providedIn: 'root',
})
export class AlertsService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  /** Cached alerts response. null = not yet fetched or invalidated. */
  readonly alertsData = signal<AlertsResponse | null>(null);

  /** Whether a fetch is in progress. */
  readonly isLoading = signal(false);

  readonly onboardingFlow = computed<Flow | null>(
    () => this.alertsData()?.flows.onboarding ?? null
  );

  readonly investmentFlow = computed<Flow | null>(
    () => this.alertsData()?.flows.investment ?? null
  );

  /** Fetch fresh alerts from API and update the signal. */
  fetchAlerts(): Observable<AlertsResponse> {
    this.isLoading.set(true);
    return this.http.get<AlertsResponse>(API_ENDPOINTS.INVESTORS_ALERTS).pipe(
      tap(response => this.alertsData.set(response)),
      finalize(() => this.isLoading.set(false))
    );
  }

  // ── Gate helpers ────────────────────────────────────────────────────

  /** Get a gate by key from the cached alerts data. */
  getGate(key: AlertTypeEnum): Gate | undefined {
    return this.alertsData()?.gates.find(g => g.key === key);
  }

  /** Whether a gate's state is 'passed'. */
  isGatePassed(key: AlertTypeEnum): boolean {
    return this.getGate(key)?.state === AlertStateEnum.PASSED;
  }

  /** Get the status string of a gate. */
  getGateStatus(key: AlertTypeEnum): string | null {
    return this.getGate(key)?.status ?? null;
  }

  /**
   * Resolves the current onboarding action to concrete route segments.
   * Uses authData for sub-route decisions (e.g. nafath initiate vs update).
   */
  resolveActionRoute(): string[] {
    const flow = this.onboardingFlow();
    if (flow?.state !== AlertStateEnum.ACTION_REQUIRED || !flow?.action) {
      return [ROUTE_PATHS.HOME];
    }

    const authData = this.authService.authData();
    return actionKeyToRoute(flow.action.key, !!authData?.nationalId);
  }

  /**
   * Checks if a given action key is the current required action.
   */
  isActionRequired(key: AlertActionEnum | AlertActionEnum[]): boolean {
    const flow = this.onboardingFlow();
    if (flow?.state !== AlertStateEnum.ACTION_REQUIRED || !flow?.action) {
      return false;
    }
    const keys = Array.isArray(key) ? key : [key];
    return keys.includes(flow.action.key);
  }

  /**
   * Whether the onboarding flow has passed (all steps complete).
   */
  readonly isOnboardingPassed = computed(
    () => this.onboardingFlow()?.state === AlertStateEnum.PASSED
  );
}
